import { getStorage, ref as storageRef, uploadString } from "firebase/storage";

/**
 * Export a chart as a PNG: download it locally and archive it under the
 * watch's folder so it shows up on the Files page.
 *
 * Uses ECharts' own `getDataURL` rather than html2canvas — it renders from the
 * chart's canvas at device pixel ratio, so exports are sharp instead of being
 * a screenshot of the DOM.
 *
 * @param {object} chartRef  ref populated by <Chart chartRef={…} />
 * @param {string} watchId
 * @param {string} name      file prefix, e.g. "ecg"
 * @returns {Promise<{ok: boolean, error?: Error}>}
 */
export const saveChartImage = async (chartRef, watchId, name) => {
  const instance = chartRef?.current?.getEchartsInstance?.();
  if (!instance) {
    return { ok: false, error: new Error("Chart is not ready yet.") };
  }

  try {
    const surface =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--wg-viz-surface")
        .trim() || "#ffffff";

    const dataUrl = instance.getDataURL({
      type: "png",
      pixelRatio: Math.max(2, window.devicePixelRatio || 1),
      backgroundColor: surface,
    });

    const stamp = new Date().toISOString();
    const filename = `${name}-${stamp}.png`;

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    await uploadString(
      storageRef(getStorage(), `charts/${watchId}/${filename}`),
      dataUrl,
      "data_url"
    );

    return { ok: true };
  } catch (error) {
    console.error("Failed to export chart image", error);
    return { ok: false, error };
  }
};

export default saveChartImage;
